import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await api.get('/auth/roles');
        setRoles(response.data.roles || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching roles:', err);
        setError('Failed to load roles. Please try again later.');
        toast.error('Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Reset form state
  const resetForm = () => {
    setName('');
    setDescription('');
    setEditingRoleId(null);
  };

  // Handle form submission for create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const roleData = {
        name,
        description
      };
      
      let response;
      
      if (editingRoleId) {
        // Update existing role
        response = await api.put(`/auth/roles/${editingRoleId}`, roleData);
        toast.success('Role updated successfully');
        
        // Update the roles list
        setRoles(prev => 
          prev.map(role => role.id === editingRoleId ? response.data.role : role)
        );
      } else {
        // Create new role
        response = await api.post('/auth/roles', roleData);
        toast.success('Role created successfully');
        
        // Add new role to the list
        setRoles(prev => [...prev, response.data.role]);
      }
      
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving role:', err);
      toast.error(err.response?.data?.message || 'Failed to save role');
    }
  };

  // Handle editing a role
  const handleEdit = (role) => {
    setEditingRoleId(role.id);
    setName(role.name);
    setDescription(role.description || '');
    setShowAddForm(true);
  };

  // Handle deleting a role
  const handleDelete = async (id) => {
    // Don't allow deleting the default roles
    const roleToDelete = roles.find(r => r.id === id);
    if (roleToDelete.name === 'user' || roleToDelete.name === 'admin') {
      toast.error('Default roles cannot be deleted');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this role? This may affect users with this role.')) {
      return;
    }
    
    try {
      await api.delete(`/auth/roles/${id}`);
      toast.success('Role deleted successfully');
      
      // Remove the deleted role from the list
      setRoles(prev => prev.filter(role => role.id !== id));
      
      // If we were editing this role, cancel edit mode
      if (editingRoleId === id) {
        setShowAddForm(false);
        resetForm();
      }
    } catch (err) {
      console.error('Error deleting role:', err);
      toast.error('Failed to delete role. It may be assigned to users.');
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
      )}
      
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-lg font-medium text-gray-900">Roles</h2>
          <p className="mt-2 text-sm text-gray-700">
            Manage user roles and permissions in the system
          </p>
        </div>
        {!showAddForm && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Role
            </button>
          </div>
        )}
      </div>
      
      {/* Role Form (Add or Edit) */}
      {showAddForm && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {editingRoleId ? 'Edit Role' : 'Add New Role'}
            </h3>
            
            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={editingRoleId && (name === 'user' || name === 'admin')}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {editingRoleId && (name === 'user' || name === 'admin') && (
                      <p className="mt-1 text-xs text-gray-500">Default role names cannot be changed</p>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Brief description of this role's permissions and responsibilities.</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {editingRoleId ? 'Update Role' : 'Add Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Roles Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {roles.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500 bg-white">
                  <p>No roles found. Click "Add Role" to create your first role.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Description
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {roles.map((role) => {
                      const isDefaultRole = role.name === 'user' || role.name === 'admin';
                      
                      return (
                        <tr key={role.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {role.name}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            {role.description || 'No description'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {isDefaultRole ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Default
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Custom
                              </span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center space-x-3 justify-end">
                              <button
                                onClick={() => handleEdit(role)}
                                className="text-gray-500 hover:text-indigo-600"
                                title="Edit role"
                              >
                                <PencilIcon className="h-5 w-5" aria-hidden="true" />
                                <span className="sr-only">Edit role</span>
                              </button>
                              <button
                                onClick={() => handleDelete(role.id)}
                                className={`text-gray-500 hover:text-red-600 ${isDefaultRole ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={isDefaultRole ? "Default roles cannot be deleted" : "Delete role"}
                                disabled={isDefaultRole}
                              >
                                <TrashIcon className="h-5 w-5" aria-hidden="true" />
                                <span className="sr-only">Delete role</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roles; 